'use client';

import { ChevronDownIcon } from '@heroicons/react/24/solid';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils/classnameVariants';
import { focusRingClasses } from '../../lib/utils/constants/accessbilityClasses';
import { textToAnchorId } from '../../lib/utils/textToAchorId';

export interface SimpleAccordionRowProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  header: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  bordered?: boolean; // optional simple border toggle
}

/**
 * SimpleAccordionRow
 * A minimal alternative to AccordionRow that only needs a header & children.
 * - Accepts arbitrary children instead of a `text` prop
 * - Uncontrolled only (no external state mgmt props)
 * - Basic open / close height animation
 */
export const SimpleAccordionRow = React.forwardRef<HTMLButtonElement, SimpleAccordionRowProps>(
  ({ header, children, defaultOpen = false, className, bordered = false, ...buttonProps }, ref) => {
    const [open, setOpen] = useState(defaultOpen);
    const [height, setHeight] = useState<'auto' | string>(defaultOpen ? 'auto' : '0px');
    const contentRef = useRef<HTMLDivElement>(null);

    const contentId = `${textToAnchorId(header)}-content`;

    const recalcHeight = useCallback(() => {
      if (!contentRef.current) return;
      if (open) {
        const scrollHeight = contentRef.current.scrollHeight;
        setHeight(`${scrollHeight}px`);
      }
    }, [open]);

    useEffect(() => {
      if (open) {
        recalcHeight();
      } else {
        setHeight('0px');
      }
    }, [open, recalcHeight]);

    // After transition to open finishes, allow auto height
    const handleTransitionEnd = () => {
      if (open) setHeight('auto');
    };

    const toggle = () => {
      if (open && contentRef.current && height === 'auto') {
        // move from auto -> fixed height for smooth collapse
        const current = contentRef.current.scrollHeight;
        setHeight(`${current}px`);
        requestAnimationFrame(() => setHeight('0px'));
      } else if (!open) {
        // prepare expand
        if (contentRef.current) {
          const target = contentRef.current.scrollHeight;
          setHeight(`${target}px`);
        }
      }
      setOpen(o => !o);
    };

    return (
      <div className={cn('flex w-full flex-col', bordered && 'border-b border-black-50')}>
        <button
          {...buttonProps}
          ref={ref}
          // base layout
          className={cn(
            'flex w-full items-start justify-between px-6 py-4 text-left font-graphik text-lg font-medium',
            focusRingClasses,
            className,
          )}
          aria-expanded={open}
          aria-controls={contentId}
          onClick={toggle}
          id={textToAnchorId(header)}
          type={buttonProps.type || 'button'}
        >
          <span className="grow break-words pr-4">{header}</span>
          <ChevronDownIcon
            className={cn('ml-2 size-5 shrink-0 transition-transform duration-200', open && 'rotate-[-180deg]')}
            aria-hidden="true"
          />
        </button>
        <div
          id={contentId}
          ref={contentRef}
          role="region"
          aria-labelledby={textToAnchorId(header)}
          aria-hidden={!open}
          style={{
            height,
            overflow: 'hidden',
            transition: 'height 0.25s ease',
          }}
          onTransitionEnd={handleTransitionEnd}
          className={cn('overflow-hidden', !open && 'pointer-events-none')}
        >
          {/* Inner wrapper holds padding so we can remove it when collapsed to avoid peek */}
          <div
            className={cn(
              'flex flex-col gap-4 px-6 pb-4 pt-0 text-black-900',
              !open && 'px-6 pb-0 pt-0', // retain horizontal padding for border alignment, remove vertical space
            )}
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);

SimpleAccordionRow.displayName = 'SimpleAccordionRow';
